/* ==========================================================================
   Trufairs Wishlist — GA4 bridge
   --------------------------------------------------------------------------
   Turns wishlist activity into GA4 ecommerce events so the standard reports
   answer "which products are wishlisted most".

   Events emitted
     add_to_wishlist        heart turned on, anywhere on the site
     remove_from_wishlist   heart turned off, or Remove on the wishlist page
     view_item_list         the wishlist page finished rendering its rows
     select_item            a wishlist row was clicked through to the PDP
     wishlist_add_to_cart   Add to cart pressed on the wishlist page

   Deliberately NOT emitted: the standard `add_to_cart`. Shopify's own GA4
   integration already sends that on every add, and a second one here would
   double-count carts. `wishlist_add_to_cart` is a distinct name, so it can
   never collide, and it is what tells you the wishlist drives revenue.

   Transport, resolved once, in this order:
     1. self-loaded gtag.js from the configured Measurement ID
     2. window.gtag              — a hard-coded Google tag is already on the page
     3. window.google_tag_manager — a real GTM container; pushed in GTM's shape
     4. nothing — events buffer for a few seconds, then drop

   Case 1 is first, and is REQUIRED for stores whose GA4 comes from Shopify's
   Google & YouTube channel. That tag lives in Shopify's sandboxed Web Pixel,
   so it puts no gtag() and no dataLayer on the storefront page: there is
   nothing here to borrow and no way to push a custom event into it. Our own
   tag is configured send_page_view:false and every event carries send_to, so
   it reports into the same property without duplicating anything the channel
   already sends.

   Nothing in this file is allowed to throw into a wishlist interaction: the
   whole bridge is passive and every handler is wrapped.
   ========================================================================== */

(function () {
  'use strict';

  var CFG = (window.TrufairsWishlistConfig && window.TrufairsWishlistConfig.ga4) || {};

  if (CFG.enabled !== true) return;

  var CURRENCY = typeof CFG.currency === 'string' && CFG.currency ? CFG.currency : 'USD';
  var LIST_ID = 'wishlist';
  var LIST_NAME = typeof CFG.listName === 'string' && CFG.listName ? CFG.listName : 'Wishlist';
  var USE_VARIANT_ID = CFG.itemIdSource === 'variant';

  /* ------------------------------------------------------------------
     Debug

     Logs in the theme editor, and on the live storefront when the URL
     carries ?wishlist_ga4_debug=1 (remembered for the tab, so you can keep
     clicking hearts across pages and still watch the events go out). Turn it
     back off with ?wishlist_ga4_debug=0.
     ------------------------------------------------------------------ */

  var DEBUG = (function () {
    try {
      var flag = /[?&]wishlist_ga4_debug=([01])/.exec(window.location.search);
      if (flag) {
        window.sessionStorage.setItem('trufairs:wishlist:ga4debug', flag[1]);
        return flag[1] === '1';
      }
      return window.sessionStorage.getItem('trufairs:wishlist:ga4debug') === '1';
    } catch (e) {
      return false;
    }
  })();

  function debug() {
    if (!DEBUG && !(window.Shopify && window.Shopify.designMode)) return;
    try {
      console.log.apply(console, ['[WishlistGA4]'].concat([].slice.call(arguments)));
    } catch (e) {
      /* console is not worth a try/catch failure */
    }
  }

  /* ------------------------------------------------------------------
     Transport

     Resolution order, and why it is this order:

       1. A configured Measurement ID. An explicit instruction from the
          merchant, so it always wins.

          This is REQUIRED when GA4 is installed through Shopify's Google &
          YouTube channel. That tag runs inside Shopify's sandboxed Web Pixel,
          which has its own window: it never defines gtag() or a dataLayer on
          the storefront page, so there is no tag here to borrow and no way to
          push a custom event into it. Before this, the bridge looked for a
          page-level tag, found none, and dropped every wishlist event in
          silence — which is why nothing reached GA4.

          We load our own gtag.js with send_page_view:false and scope every
          event with send_to, so wishlist events land in the same property
          alongside the channel's, and nothing the channel already sends
          (page_view, view_item, add_to_cart, purchase) is duplicated.

       2. A real gtag() already on the page — a hard-coded Google tag.

       3. A real GTM container, verified through window.google_tag_manager
          rather than the presence of window.dataLayer. Plenty of apps create
          a bare dataLayer array with no container behind it; pushing into one
          of those loses events just as silently.

       4. Nothing yet. Events are buffered and re-checked for a few seconds in
          case a tag is still loading, then dropped.
     ------------------------------------------------------------------ */

  var MEASUREMENT_ID = /^G-[A-Z0-9]{4,20}$/.test(String(CFG.measurementId || ''))
    ? String(CFG.measurementId)
    : '';

  var transport = null;
  var pending = [];
  var PENDING_MAX = 50;
  var retries = 0;
  var RETRY_MAX = 10;
  var retryTimer = null;

  function ensureGtagStub() {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
      // The canonical stub. Must push `arguments` itself, not an array copy.
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
    }
  }

  function gtagJsAlreadyLoading() {
    try {
      return !!document.querySelector('script[src*="googletagmanager.com/gtag/js"]');
    } catch (e) {
      return false;
    }
  }

  function loadOwnTag() {
    ensureGtagStub();
    window.gtag('js', new Date());
    // send_page_view:false — this tag exists only to carry wishlist events.
    window.gtag('config', MEASUREMENT_ID, { send_page_view: false });

    // Commands queue in dataLayer, so config before the script is fine, and
    // a gtag.js another tag is already pulling in is not fetched twice.
    if (!gtagJsAlreadyLoading()) {
      var script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(MEASUREMENT_ID);
      (document.head || document.documentElement).appendChild(script);
      debug('loaded gtag.js for', MEASUREMENT_ID);
    } else {
      debug('gtag.js already on the page, configured', MEASUREMENT_ID);
    }
  }

  function hasGtm() {
    // A container writes an object here keyed by its GTM-XXXX id. A bare
    // dataLayer array left behind by some other app does not.
    var gtm = window.google_tag_manager;
    if (!gtm || typeof gtm !== 'object') return false;
    for (var key in gtm) {
      if (key.indexOf('GTM-') === 0) return true;
    }
    return false;
  }

  function resolveTransport() {
    if (transport) return transport;

    if (MEASUREMENT_ID) {
      loadOwnTag();
      transport = function (name, params) {
        // send_to keeps these events on the configured property even if some
        // other tag on the page configures a different one.
        params.send_to = MEASUREMENT_ID;
        window.gtag('event', name, params);
      };
      debug('transport: own gtag for', MEASUREMENT_ID);
      return transport;
    }

    if (typeof window.gtag === 'function') {
      transport = function (name, params) {
        window.gtag('event', name, params);
      };
      debug('transport: existing gtag');
      return transport;
    }

    if (hasGtm() && Array.isArray(window.dataLayer)) {
      // GTM's GA4 ecommerce convention: items live under `ecommerce`, and the
      // previous ecommerce object is cleared first so tags never read stale
      // items from an earlier push.
      transport = function (name, params) {
        var payload = { event: name };
        var ecommerce = {};
        var key;
        for (key in params) {
          if (!Object.prototype.hasOwnProperty.call(params, key)) continue;
          ecommerce[key] = params[key];
        }
        window.dataLayer.push({ ecommerce: null });
        payload.ecommerce = ecommerce;
        window.dataLayer.push(payload);
      };
      debug('transport: GTM dataLayer');
      return transport;
    }

    return null;
  }

  function flushPending() {
    if (!pending.length) return;
    var fn = resolveTransport();
    if (!fn) return;
    var queued = pending;
    pending = [];
    queued.forEach(function (entry) {
      try {
        fn(entry.name, entry.params);
        debug('flushed', entry.name, entry.params);
      } catch (e) {
        /* passive */
      }
    });
  }

  function scheduleRetry() {
    if (retryTimer || retries >= RETRY_MAX) return;
    retryTimer = window.setTimeout(function () {
      retryTimer = null;
      retries++;
      if (resolveTransport()) {
        flushPending();
        return;
      }
      if (retries >= RETRY_MAX) {
        debug(
          'no Google tag found after ' + retries + ' checks and no Measurement ID ' +
            'configured — ' + pending.length + ' event(s) dropped. If GA4 is installed ' +
            'through the Google & YouTube channel, set the GA4 Measurement ID in ' +
            'Theme settings > Wishlist.'
        );
        pending = [];
        return;
      }
      scheduleRetry();
    }, 500);
  }

  function send(name, params) {
    try {
      var fn = resolveTransport();
      if (!fn) {
        if (pending.length < PENDING_MAX) pending.push({ name: name, params: params });
        scheduleRetry();
        return;
      }
      flushPending();
      fn(name, params);
      debug(name, params);
    } catch (e) {
      /* Analytics must never break the wishlist. */
    }
  }

  /* ------------------------------------------------------------------
     Item mapping
     ------------------------------------------------------------------ */

  /**
   * Shopify stores prices in minor units; GA4 wants a major-unit number.
   * Returns undefined rather than 0 when the price is unknown, so an
   * unpriced item is absent from reports instead of showing as free.
   */
  function toMajorUnits(cents) {
    if (cents == null || cents === '') return undefined;
    var n = typeof cents === 'number' ? cents : parseInt(String(cents), 10);
    if (!isFinite(n) || n < 0) return undefined;
    return Math.round(n) / 100;
  }

  /**
   * Build one GA4 item. `source` is a wishlist entry or a plain object with
   * the same keys. GA4 rejects undefined values silently, so keys are only
   * set when there is a real value behind them.
   */
  function toItem(source, extra) {
    if (!source || source.id == null) return null;

    var id = USE_VARIANT_ID && source.variantId ? String(source.variantId) : String(source.id);
    var item = { item_id: id };

    if (source.title) item.item_name = String(source.title);
    if (source.vendor) item.item_brand = String(source.vendor);
    if (source.type) item.item_category = String(source.type);
    if (source.variantId) item.item_variant = String(source.variantId);

    var price = toMajorUnits(source.price);
    if (price !== undefined) item.price = price;

    item.quantity = 1;

    if (extra) {
      for (var key in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, key)) item[key] = extra[key];
      }
    }
    return item;
  }

  function eventFor(items) {
    var value = 0;
    var priced = false;

    items.forEach(function (item) {
      if (typeof item.price === 'number') {
        priced = true;
        value += item.price * (item.quantity || 1);
      }
    });

    var payload = { currency: CURRENCY, items: items };
    // Only claim a value when at least one item actually carried a price.
    if (priced) payload.value = Math.round(value * 100) / 100;
    return payload;
  }

  /* ------------------------------------------------------------------
     add_to_wishlist / remove_from_wishlist
     ------------------------------------------------------------------ */

  document.addEventListener('wishlist:change', function (evt) {
    try {
      var detail = evt && evt.detail;
      if (!detail) return;

      // `prune` is housekeeping for a product that no longer exists in
      // Shopify, and `clear` is a bulk action, not a per-product signal.
      // Neither is a customer intent, so neither is reported.
      if (detail.action !== 'add' && detail.action !== 'remove') return;

      var source = detail.item;
      if (!source && detail.productId) {
        // Defensive: an integrator calling the public API directly may emit
        // without an entry. Fall back to the current list.
        var list = detail.items || [];
        for (var i = 0; i < list.length; i++) {
          if (String(list[i].id) === String(detail.productId)) {
            source = list[i];
            break;
          }
        }
      }

      var item = toItem(source);
      if (!item) return;

      send(
        detail.action === 'add' ? 'add_to_wishlist' : 'remove_from_wishlist',
        eventFor([item])
      );
    } catch (e) {
      /* passive */
    }
  });

  /* ------------------------------------------------------------------
     Wishlist page: view_item_list, select_item, wishlist_add_to_cart
     ------------------------------------------------------------------ */

  var ROW = '[data-wishlist-row]';

  function itemFromRow(row, position) {
    if (!row) return null;
    return toItem(
      {
        id: row.getAttribute('data-product-id'),
        title: row.getAttribute('data-product-title'),
        vendor: row.getAttribute('data-product-vendor'),
        type: row.getAttribute('data-product-type'),
        variantId: row.getAttribute('data-variant-id'),
        price: row.getAttribute('data-product-price')
      },
      {
        item_list_id: LIST_ID,
        item_list_name: LIST_NAME,
        index: position
      }
    );
  }

  document.addEventListener('wishlist:page-rendered', function () {
    try {
      var rows = document.querySelectorAll(ROW);
      if (!rows.length) return;

      var items = [];
      for (var i = 0; i < rows.length; i++) {
        var item = itemFromRow(rows[i], i);
        if (item) items.push(item);
      }
      if (!items.length) return;

      var payload = eventFor(items);
      payload.item_list_id = LIST_ID;
      payload.item_list_name = LIST_NAME;
      send('view_item_list', payload);
    } catch (e) {
      /* passive */
    }
  });

  document.addEventListener(
    'click',
    function (evt) {
      try {
        var target = evt.target;
        if (!target || !target.closest) return;

        var row = target.closest(ROW);
        if (!row) return;

        // A heart inside the row is a wishlist toggle, already reported by
        // wishlist:change. Only a real navigation is a select_item.
        if (target.closest('[data-wishlist-button]')) return;

        var link = target.closest('a[href]');
        if (!link || !row.contains(link)) return;

        var rows = row.parentNode ? row.parentNode.querySelectorAll(ROW) : [row];
        var position = Array.prototype.indexOf.call(rows, row);

        var item = itemFromRow(row, position < 0 ? 0 : position);
        if (!item) return;

        var payload = eventFor([item]);
        payload.item_list_id = LIST_ID;
        payload.item_list_name = LIST_NAME;
        send('select_item', payload);
      } catch (e) {
        /* passive */
      }
    },
    true
  );

  document.addEventListener('ajaxProduct:added', function (evt) {
    try {
      var detail = evt && evt.detail;
      var btn = detail && detail.addToCartBtn;
      if (!btn || !btn.closest) return;

      // Fires site-wide. Only an add that came from a wishlist row is ours.
      var row = btn.closest(ROW);
      if (!row) return;

      var item = itemFromRow(row, 0);
      if (!item) return;

      var quantity = parseInt(detail.quantity, 10);
      item.quantity = isFinite(quantity) && quantity > 0 ? quantity : 1;

      send('wishlist_add_to_cart', eventFor([item]));
    } catch (e) {
      /* passive */
    }
  });

  debug('bridge ready', {
    itemIdSource: USE_VARIANT_ID ? 'variant' : 'product',
    currency: CURRENCY,
    measurementId: MEASUREMENT_ID || '(using existing tag)'
  });
})();
